/**
 * RFC 5545 rendering for the season calendar. One pure function, no I/O, feeding both the
 * subscribed feed and the one-shot `.ics` download — the file you download and the feed you
 * subscribe to must not be able to disagree about who is on court.
 *
 * Derives nothing new: `effectiveRoster` already resolves subs and fills, and `playerStatus`
 * already answers where one player stands. This module is the wire format and nothing else.
 */
import { courtTimePhrase } from './reminder.js';
import { effectiveRoster, playerStatus } from './roster.js';
import type { GroupBootstrap, IsoDate, PlayerId } from './types.js';

/**
 * What Apple and Google title the subscribed calendar. The same string for every player, every
 * group and every season: this lands in a list beside *Work* and *Family*, a list that names
 * contents rather than publishers, and it is what the group already calls the thing — the
 * weekly reminder and every event description spell out the same phrase.
 */
export const CALENDAR_NAME = 'Permanent Court Time';

/**
 * Both groups play here, so the club is a fact about this app rather than about a group or a
 * season — a constant instead of a `location` column in `groups.csv` that would be the same in
 * every row. A calendar event with no *where* is half an event: it is what makes the phone
 * offer directions.
 */
export const CALENDAR_LOCATION = 'LaTuchie Tennis Center';

/** Last line of every description, so a player wondering what put this on their phone can tell. */
export const CALENDAR_ORIGIN = 'topspinapp.com';

const UID_DOMAIN = 'topspinapp.com';
const PRODID = '-//Topspin//Season Calendar//EN';

/**
 * `SEQUENCE` counts whole hours from here, which is enough for a re-imported `.ics` to read as
 * an update rather than a duplicate. It is also the `LAST-MODIFIED` an untouched night reports:
 * a session carries no import timestamp of its own, and stamping it with the render clock
 * instead would churn every event's `SEQUENCE` on every poll.
 */
export const CALENDAR_EPOCH = '2020-01-01T00:00:00.000Z';

/**
 * The only zone either group plays in, and the only one {@link VTIMEZONES} below has a block
 * for. Named here so the download builder, which has no server config to read, doesn't guess.
 */
export const CALENDAR_TIME_ZONE = 'America/New_York';

const MAX_OCTETS = 75;
const CRLF = '\r\n';

const encoder = new TextEncoder();
const decoder = new TextDecoder();

/**
 * Timezone definitions, by `TZID`. A named `TZID` with no `VTIMEZONE` is a spec violation that
 * Apple tolerates and Outlook does not, and the alternative — converting to UTC instants —
 * needs per-date DST arithmetic here for a season that straddles two changeovers. This hands
 * that to the client's tzdata instead.
 *
 * Written out rather than derived: `Intl` will tell you an offset for a date, but not the rule
 * that produced it, and guessing at the rule is how a season ends up an hour out for one week
 * in March. Another zone is another entry, and an unknown one throws rather than shipping a
 * block that is quietly wrong.
 */
const VTIMEZONES: Record<string, string[]> = {
  [CALENDAR_TIME_ZONE]: [
    'BEGIN:VTIMEZONE',
    `TZID:${CALENDAR_TIME_ZONE}`,
    'BEGIN:DAYLIGHT',
    'TZOFFSETFROM:-0500',
    'TZOFFSETTO:-0400',
    'TZNAME:EDT',
    'DTSTART:19700308T020000',
    'RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=2SU',
    'END:DAYLIGHT',
    'BEGIN:STANDARD',
    'TZOFFSETFROM:-0400',
    'TZOFFSETTO:-0500',
    'TZNAME:EST',
    'DTSTART:19701101T020000',
    'RRULE:FREQ=YEARLY;BYMONTH=11;BYDAY=1SU',
    'END:STANDARD',
    'END:VTIMEZONE',
  ],
};

export interface CalendarOptions {
  /** Whose calendar this is. Undefined → group-generic events, no personal status. */
  playerId?: PlayerId;
  /** `PT1H` for a feed; omitted for a downloaded file, which has nothing to refresh from. */
  refreshInterval?: string;
  /** Injected so tests get a fixed DTSTAMP. */
  now: Date;
  timeZone: string;
}

/**
 * The whole season as one `VCALENDAR`, across however many groups the caller was handed.
 *
 * Takes the shape `GET /bootstrap` already returns per group, which is exactly what the web app
 * holds and what the feed handler loads — so neither caller reshapes anything.
 */
export function renderSeasonCalendar(
  groups: GroupBootstrap[],
  options: CalendarOptions,
): string {
  const vtimezone = VTIMEZONES[options.timeZone];
  if (!vtimezone) {
    throw new Error(`no VTIMEZONE definition for ${options.timeZone}`);
  }

  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    `PRODID:${PRODID}`,
    'CALSCALE:GREGORIAN',
    `X-WR-CALNAME:${escapeText(CALENDAR_NAME)}`,
    `X-WR-TIMEZONE:${options.timeZone}`,
  ];
  if (options.refreshInterval) {
    // Hints, both of them: Apple honours these, Google ignores them and polls on its own
    // famously lazy schedule. `X-PUBLISHED-TTL` is the pre-standard twin some clients still read.
    lines.push(`REFRESH-INTERVAL;VALUE=DURATION:${options.refreshInterval}`);
    lines.push(`X-PUBLISHED-TTL:${options.refreshInterval}`);
  }
  lines.push(...vtimezone);

  for (const event of collectEvents(groups, options)) {
    lines.push(...renderEvent(event, options));
  }

  lines.push('END:VCALENDAR');
  // CRLF everywhere, including after the last line.
  return lines.map(fold).join(CRLF) + CRLF;
}

interface CalendarEvent {
  uid: string;
  date: IsoDate;
  groupId: string;
  start: string;
  end: string;
  summary: string;
  description: string;
  transparent: boolean;
  lastModified: string;
}

function collectEvents(groups: GroupBootstrap[], options: CalendarOptions): CalendarEvent[] {
  const events: CalendarEvent[] = [];

  for (const group of groups) {
    const { config, sessions, overrides, availabilities, fills } = group;
    for (const session of sessions) {
      // A night nothing happens on has nothing to put on a calendar — the note ("Thanksgiving")
      // has no calendar to be on, and "no tennis" events would fill the season with non-events.
      if (session.status !== 'scheduled') continue;

      const roster = effectiveRoster(config, session, overrides, availabilities, fills);
      const status = options.playerId
        ? playerStatus(session, overrides, options.playerId)
        : undefined;
      // A player who isn't in this session at all — not on the roster, most often because a new
      // season rolled over without them — gets the group-generic event rather than a 404 or a
      // claim about where they stand.
      const onCourt = status === 'playing' || status === 'subbing';
      const offCourt = status === 'sitting' || status === 'out';

      events.push({
        uid: `${config.groupId}-${session.date}@${UID_DOMAIN}`,
        date: session.date,
        groupId: config.groupId,
        // The session's `time` override where present; the end is always the group's `endTime`,
        // which is when the court booking ends and so holds even on a week whose start moved.
        start: localStamp(session.date, roster.time),
        end: localStamp(session.date, config.endTime),
        summary: offCourt ? 'Tennis (not playing)' : 'Tennis',
        description: describe(config.name, roster),
        // A night the player isn't on court still shows on the calendar and still doesn't make
        // them look busy to anyone reading their free/busy. That is what makes "the whole
        // season" the right default rather than a nuisance.
        transparent: !onCourt,
        lastModified: lastTouched(group, session.date),
      });
    }
  }

  // Date first so a two-group calendar reads in the order the nights arrive; group id breaks
  // the tie, so two renders of the same data are byte-identical.
  return events.sort(
    (a, b) => a.date.localeCompare(b.date) || a.groupId.localeCompare(b.groupId),
  );
}

function describe(groupName: string, roster: ReturnType<typeof effectiveRoster>): string {
  const lineup = roster.playing.map((slot) =>
    slot.inForName ? `• ${slot.name} (in for ${slot.inForName})` : `• ${slot.name}`,
  );
  // A night the schedule left short and nobody has covered yet is four bullets short a bullet,
  // and says so — the same loose end the reminder leaves in the group's hands.
  if (roster.needed > 0) lineup.push(`• Needs ${roster.needed} more`);

  return [courtTimePhrase(groupName), '', 'Lineup:', ...lineup, '', CALENDAR_ORIGIN].join('\n');
}

/**
 * The newest edit touching this date, across the overrides, availability answers and fills —
 * everything that can change who the event says is playing. {@link CALENDAR_EPOCH} for a night
 * nobody has touched.
 */
function lastTouched(group: GroupBootstrap, date?: IsoDate): string {
  let newest = CALENDAR_EPOCH;
  const consider = (at: string | undefined) => {
    if (at && at > newest) newest = at;
  };
  const wanted = (on: IsoDate) => date === undefined || on === date;
  for (const o of group.overrides) if (wanted(o.date)) consider(o.updatedAt);
  for (const a of group.availabilities) if (wanted(a.date)) consider(a.updatedAt);
  for (const f of group.fills) if (wanted(f.date)) consider(f.updatedAt);
  return newest;
}

/**
 * The newest edit anywhere in this data, which is what a *feed* should pass as `now`.
 *
 * A subscribed calendar is fetched hourly and answered with an `ETag`, so the body has to be
 * byte-identical between real changes or the `If-None-Match` round trip can never produce a
 * 304 and every poll re-downloads the season. Stamping `DTSTAMP` with the wall clock would do
 * exactly that. A downloaded file has nothing to revalidate against, so it passes the real
 * clock and says truthfully when it was written.
 */
export function latestChange(groups: GroupBootstrap[]): Date {
  let newest = CALENDAR_EPOCH;
  for (const group of groups) {
    const touched = lastTouched(group);
    if (touched > newest) newest = touched;
  }
  return new Date(newest);
}

function renderEvent(event: CalendarEvent, options: CalendarOptions): string[] {
  return [
    'BEGIN:VEVENT',
    `UID:${event.uid}`,
    `DTSTAMP:${utcStamp(options.now)}`,
    `DTSTART;TZID=${options.timeZone}:${event.start}`,
    `DTEND;TZID=${options.timeZone}:${event.end}`,
    `SUMMARY:${escapeText(event.summary)}`,
    `LOCATION:${escapeText(CALENDAR_LOCATION)}`,
    `DESCRIPTION:${escapeText(event.description)}`,
    `LAST-MODIFIED:${utcStamp(new Date(event.lastModified))}`,
    `SEQUENCE:${sequenceOf(event.lastModified)}`,
    `TRANSP:${event.transparent ? 'TRANSPARENT' : 'OPAQUE'}`,
    // No VALARM: the group already gets a morning reminder, and putting alerts on somebody
    // else's phone is a decision they should make in their own calendar app, where they can.
    // No ATTENDEE, ORGANIZER or METHOD: any of the three makes some clients treat this as a
    // meeting invitation, with an RSVP this app has no way to hear the answer to.
    'END:VEVENT',
  ];
}

/**
 * Whole hours since {@link CALENDAR_EPOCH}. Monotonic in practice, and enough for a re-imported
 * file to read as an update. It can move backwards if the newest edit on a date is deleted; a
 * client that then ignores one update is a cosmetic loss on a file the player was told is a
 * snapshot anyway.
 */
export function sequenceOf(lastModified: string): number {
  const hours = Math.floor(
    (Date.parse(lastModified) - Date.parse(CALENDAR_EPOCH)) / 3_600_000,
  );
  return Number.isFinite(hours) ? Math.max(0, hours) : 0;
}

/** `2026-09-08`, `18:30` → `20260908T183000`, to be read against the event's `TZID`. */
function localStamp(date: IsoDate, hhmm: string): string {
  return `${date.replace(/-/g, '')}T${hhmm.replace(':', '')}00`;
}

function utcStamp(at: Date): string {
  return `${at.toISOString().slice(0, 19).replace(/[-:]/g, '')}Z`;
}

/**
 * TEXT escaping. Colons are deliberately not escaped — the spec doesn't ask for it in a value,
 * and clients that see `\:` show the backslash. The realistic source of a stray comma is an
 * outsider sub's name, which is free text a player typed on their phone.
 */
export function escapeText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r\n|\r|\n/g, '\\n');
}

/**
 * Folding at 75 **octets**, not characters, continued with CRLF and one space. A roster name
 * with an accent in it is multi-byte, and splitting mid-sequence corrupts the name in every
 * client — so a split backs off over any UTF-8 continuation byte.
 */
export function fold(line: string): string {
  const bytes = encoder.encode(line);
  if (bytes.length <= MAX_OCTETS) return line;

  const parts: string[] = [];
  let start = 0;
  // The first line gets all 75; every continuation spends one on its leading space.
  let budget = MAX_OCTETS;
  while (start < bytes.length) {
    let end = Math.min(start + budget, bytes.length);
    while (end > start + 1 && end < bytes.length && ((bytes[end] as number) & 0xc0) === 0x80) {
      end -= 1;
    }
    parts.push(decoder.decode(bytes.subarray(start, end)));
    start = end;
    budget = MAX_OCTETS - 1;
  }
  return parts.join(`${CRLF} `);
}
